import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaPiggyBank, FaEgg, FaLeaf, FaShieldAlt, FaUsers, FaNewspaper, FaChevronDown, FaDrumstickBite, FaArrowUp, FaArrowDown, FaHeart, FaFilter, FaFish, FaPaw, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { GiCow } from 'react-icons/gi';
import { IconType } from 'react-icons/lib';
import Footer from '../components/Footer';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { getProducts } from '../api/products';
import { getNews } from '../api/news';
import type { Product, News } from '../api/types';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const Container = styled.div<{ $showFooter: boolean }>`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
`;

const SlidesContainer = styled.div<{ $currentSlide: number }>`
  width: 100%;
  height: 100vh;
  transform: translateY(-${props => props.$currentSlide * 100}vh);
  transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
`;

const Slide = styled.section`
  width: 100%;
  height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

// 스크롤에 따라 나타나는 배경 로고
const BackgroundLogo = styled(motion.div)<{ $visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20rem;
  font-weight: 900;
  color: ${props => props.theme.colors.primary};
  opacity: ${props => props.$visible ? 0.3 : 0};
  z-index: 0;
  pointer-events: none;
  transition: opacity 0.6s ease;
  user-select: none;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 15rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 10rem;
  }
`;

// HERO 섹션
const HeroSlide = styled(Slide)`
  position: relative;
  color: white;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* background removed - using video element instead */
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 200, 87, 0.1), rgba(255, 140, 0, 0.1));
    z-index: 1;
  }
`;
const HeroBackgroundVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  z-index: 2;
  padding: ${props => props.theme.spacing.lg};
  max-width: 900px;
`;

const HeroTitle = styled(motion.h1)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 4rem;
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: 500;
  line-height: 1.2;
  text-shadow: 3px 3px 12px rgba(0, 0, 0, 0.9), 1px 1px 3px rgba(0, 0, 0, 1);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const HeroSlogan = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: ${props => props.theme.spacing.xl};
  opacity: 0.95;
  font-weight: 300;
  letter-spacing: 1px;
  text-shadow: 2px 2px 10px rgba(0, 0, 0, 0.9), 1px 1px 3px rgba(0, 0, 0, 1);
  white-space: pre-line;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  cursor: pointer;
`;

const ScrollText = styled.span`
  font-size: 0.9rem;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ScrollArrow = styled(motion.div)`
  font-size: 1.5rem;
`;

// 슬라이드 섹션들
const SlideSection = styled(Slide)<{ backgroundColor?: string }>`
  background: ${props => props.backgroundColor || 'white'};
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
`;

const SectionContent = styled.div`
  max-width: 1280px;
  width: 100%;
  text-align: center;
`;

const SectionTitle = styled(motion.h2)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 3rem;
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

// 기업 소개 섹션
const AboutSlide = styled(SlideSection)`
  position: relative;
  background: linear-gradient(135deg, rgba(255, 200, 87, 0.05), rgba(255, 140, 0, 0.05));
`;

const AboutContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  padding: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }
`;

const AboutTextSection = styled.div`
  text-align: left;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

const AboutImageSection = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  /* 점선 프레임 1 - 큰 원 */
  &::before {
    content: '';
    position: absolute;
    width: 550px;
    height: 550px;
    border: 4px dashed rgba(255, 200, 87, 0.6);
    border-radius: 50%;
    z-index: 0;
    right: -50px;
    bottom: -50px;
    animation: rotate-slow 20s linear infinite;
  }

  /* 점선 프레임 2 - 작은 원 */
  &::after {
    content: '';
    position: absolute;
    width: 450px;
    height: 450px;
    border: 3px dashed rgba(255, 140, 0, 0.5);
    border-radius: 50%;
    z-index: 0;
    right: -30px;
    bottom: -30px;
    animation: rotate-slow 15s linear infinite reverse;
  }

  img {
    width: 100%;
    max-width: 650px;
    height: auto;
    object-fit: contain;
    position: relative;
    z-index: 1;
    border-radius: 40px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 250, 240, 0.9) 100%);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
    animation: float 3s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes rotate-slow {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    order: -1;

    &::before {
      width: 400px;
      height: 400px;
    }

    &::after {
      width: 320px;
      height: 320px;
    }

    img {
      max-width: 450px;
      border-radius: 30px;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &::before {
      width: 300px;
      height: 300px;
    }

    &::after {
      width: 240px;
      height: 240px;
    }

    img {
      max-width: 350px;
      border-radius: 20px;
    }
  }
`;

const AboutHeading = styled(motion.h2)`
  font-family: 'Gamwulchi Free Gothic', 'Noto Sans KR', sans-serif;
  font-size: 4.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xl};
  line-height: 1.2;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 3.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.8rem;
  }
`;

const AboutText = styled(motion.p)`
  font-size: 1.6rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xxl};
  word-break: keep-all;
  white-space: pre-line;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.4rem;
    line-height: 1.7;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    line-height: 1.6;
  }
`;

const LearnMoreButton = styled(motion.button)`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xxl};
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  background: ${props => props.theme.colors.primary};
  border: none;
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.normal};
  box-shadow: ${props => props.theme.shadows.lg};

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xl};
    font-size: 1rem;
  }
`;

// 제품 섹션
const ProductsSlide = styled(SlideSection)`
  background: white;
`;

const CategoryTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.$active ? props.theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: 500;
  transition: all ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: 1rem;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.$active ? props.theme.colors.primaryDark : props.theme.colors.background};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 6px 12px;
    font-size: 0.85rem;
    gap: 4px;
    border-width: 1.5px;
  }
`;

const ProductsSliderContainer = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
  padding: 0 40px;

  .swiper {
    padding: 20px 0 40px;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: ${props => props.theme.colors.primary};
    &:after {
      font-size: 2rem;
    }
  }

  .swiper-pagination-bullet {
    background: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 20px;
  }
`;

const ProductCard = styled(motion.div)`
  background: ${props => props.theme.colors.background};
  border-radius: 4px; /* 비디오 아티팩트 감소를 위해 작게 조정 */
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};
  transition: all ${props => props.theme.transitions.normal};
  cursor: pointer;
  height: 100%;

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const ProductImageContainer = styled.div<{ $bgColor?: string; $bgImage?: string }>`
  height: 250px;
  background: ${props => props.$bgImage
    ? `url(${props.$bgImage}) center/cover no-repeat`
    : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
  overflow: hidden; /* border-radius에서 비디오 아티팩트 숨김 */
`;
const ProductMediaVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: high-quality;
  image-rendering: -webkit-optimize-contrast;
`;


// Lazy loading video component
const LazyVideo: React.FC<{
  src: string;
  className?: string;
}> = ({ src, className }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {
              // 자동 재생 실패 시 무시
            });
          } else {
            video.pause();
          }
        });
      },
      {
        threshold: 0.5, // 50% 보일 때 재생
      }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <ProductMediaVideo
      ref={videoRef}
      src={src}
      loop
      muted
      playsInline
      preload="none"
      className={className}
    />
  );
};
const ProductMediaImage = styled.div<{ $bgImage: string }>`
  width: 100%;
  height: 100%;
  background: ${props => `url(${props.$bgImage}) center/cover no-repeat`};
`;

const ProductInfo = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const ProductTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ProductDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.md};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ViewMoreButton = styled.button`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  padding: ${props => props.theme.spacing.sm} 0;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

// 새소식 섹션
const NewsSlide = styled(SlideSection)`
  background: ${props => props.theme.colors.background};
`;

const NewsSliderContainer = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
  padding: 0 40px;

  .swiper {
    padding: 20px 0 40px;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: ${props => props.theme.colors.primary};
    &:after {
      font-size: 2rem;
    }
  }

  .swiper-pagination-bullet {
    background: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 20px;
  }
`;

const NewsCard = styled(motion.div)`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};
  transition: all ${props => props.theme.transitions.normal};
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const NewsImage = styled.div<{ $bgColor?: string; $thumbnailUrl?: string }>`
  height: 200px;
  background: ${props => props.$thumbnailUrl
    ? `url(${props.$thumbnailUrl}) center/cover no-repeat`
    : props.$bgColor || `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.accent})`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  position: relative;
`;

const NewsDateBadge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  background: rgba(255, 255, 255, 0.95);
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const NewsContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const NewsTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
  line-height: 1.4;
`;

const NewsExcerpt = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

// 문의 섹션
const SupportSlide = styled(SlideSection)<{ $isActive: boolean }>`
  background: linear-gradient(135deg, rgba(255, 200, 87, 0.08), rgba(255, 140, 0, 0.08));
  overflow-y: ${props => props.$isActive ? 'auto' : 'hidden'};

  /* 모바일 환경에서만 스크롤바 숨기기 */
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const SupportContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const ContactInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xxl};
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const ContactInfoCard = styled(motion.div)`
  background: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  transition: all ${props => props.theme.transitions.normal};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const ContactIcon = styled.div`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ContactLabel = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ContactValue = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
`;

const ContactButton = styled(motion.button)`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadows.md};
  transition: all ${props => props.theme.transitions.normal};

  &:hover {
    background: ${props => props.theme.colors.accent};
    box-shadow: ${props => props.theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

// 슬라이드 네비게이션
const SlideNavigation = styled.div`
  position: fixed;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    right: 20px;
    gap: 10px;
  }
`;

const NavDot = styled.button<{ $active: boolean; $currentSlide: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid ${props => {
    if (props.$active) return props.theme.colors.primary;
    // 2, 3, 4번 슬라이드 (인덱스 1, 2, 3)에서는 검은색 테두리
    return props.$currentSlide >= 1 ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.5)';
  }};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.2);
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: transparent;
  }
`;

const SlideControls = styled.div`
  position: fixed;
  right: 30px;
  bottom: 30px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    right: 20px;
    bottom: 20px;
  }
`;

const ControlButton = styled.button`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 16px;

  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// 모바일 스와이프 가이드
const SwipeGuide = styled(motion.div)<{ $show: boolean }>`
  position: fixed;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999;
  color: white;
  text-align: center;
  font-size: 0.85rem;
  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.5s ease;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;


// 제품 ID별 이미지 매핑 (고화질 WebM 사용)
const productImageMap: Record<number, string> = {
  1: '/img/main_pig5.webm',
  2: '/img/main_pig2.webm',
  3: '/img/main_pig6.webm',
  4: '/img/main_cow7.webm',
  5: '/img/main_cow6.webm',
  6: '/img/main_cow9.webm',
  7: '/img/main_chicken2l.webm',
  8: '/img/main_sheep1.webm',
  9: '/img/main_dog1.webm',
};

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [showSwipeGuide, setShowSwipeGuide] = useState(true);
  const [showFooter, setShowFooter] = useState(false);
  const totalSlides = 5; // Hero, About, Products, News, Support

  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const supportSlideRef = useRef<HTMLDivElement>(null);

  // Hero 비디오 재생/정지 제어
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    if (currentSlide === 0) {
      video.play().catch(() => {
        // 자동 재생 실패 시 무시
      });
    } else {
      video.pause();
    }
  }, [currentSlide]);

  // 슬라이드 변경 시 마지막 슬라이드 스크롤 리셋
  useEffect(() => {
    if (supportSlideRef.current && currentSlide === 4) {
      supportSlideRef.current.scrollTop = 0;
    }
  }, [currentSlide]);

  // 스와이프 가이드 자동 숨김
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSwipeGuide(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // 페이지 기본 스크롤 차단 (슬라이드 전환만 허용)
  // iOS Safari 호환 방식 - position: fixed 대신 overflow + height 사용
  useEffect(() => {
    // HomePage 마운트 시 스크롤 차단
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.body.style.touchAction = 'none'; // iOS Safari 터치 스크롤 차단

    return () => {
      // HomePage 언마운트 시 스크롤 복원
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    };
  }, []);

  // API에서 제품 데이터 가져오기
  useEffect(() => {
    const fetchProductsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getProducts({ lang: i18n.language });
        setProducts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
        console.error('Failed to fetch products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsData();
  }, [i18n.language]);

  // API에서 뉴스 데이터 가져오기
  useEffect(() => {
    const fetchNewsData = async () => {
      setNewsLoading(true);
      try {
        const data = await getNews({ published: true, lang: i18n.language });
        setNewsData(data.slice(0, 6)); // 최신 6개만 표시
      } catch (err) {
        console.error('Failed to fetch news:', err);
      } finally {
        setNewsLoading(false);
      }
    };

    fetchNewsData();
  }, [i18n.language]);

  // Type cast icons
  const PigIcon = FaPiggyBank as IconType;
  const ChickenIcon = FaEgg as IconType;
  const CowIcon2 = GiCow as IconType;
  const DrumstickIcon = FaDrumstickBite as IconType;
  const LeafIcon = FaLeaf as IconType;
  const ShieldIcon = FaShieldAlt as IconType;
  const UsersIcon = FaUsers as IconType;
  const NewsIcon = FaNewspaper as IconType;
  const ChevronDownIcon = FaChevronDown as IconType;
  const ArrowUpIcon = FaArrowUp as IconType;
  const ArrowDownIcon = FaArrowDown as IconType;
  const HeartIcon = FaHeart as IconType;
  const FilterIcon = FaFilter as IconType;
  const FishIcon = FaFish as IconType;
  const PawIcon = FaPaw as IconType;

  const [activeCategory, setActiveCategory] = useState('전체');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newsData, setNewsData] = useState<News[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);

  // 슬라이드 전환 함수
  const goToSlide = useCallback((slideIndex: number) => {
    if (slideIndex >= 0 && slideIndex < totalSlides && !isScrolling) {
      setCurrentSlide(slideIndex);
      // Header에 슬라이드 변경 알림
      window.dispatchEvent(new CustomEvent('slideChange', { detail: { currentSlide: slideIndex } }));
    }
  }, [totalSlides, isScrolling]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1 && !isScrolling) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      // Header에 슬라이드 변경 알림
      window.dispatchEvent(new CustomEvent('slideChange', { detail: { currentSlide: newSlide } }));
    }
  }, [currentSlide, totalSlides, isScrolling]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0 && !isScrolling) {
      const newSlide = currentSlide - 1;
      setCurrentSlide(newSlide);
      // Header에 슬라이드 변경 알림
      window.dispatchEvent(new CustomEvent('slideChange', { detail: { currentSlide: newSlide } }));
    }
  }, [currentSlide, isScrolling]);

  // 터치 제스처 및 이벤트 처리
  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;
    const minSwipeDistance = 50;

    // 마지막 슬라이드의 스크롤 위치 체크
    const isAtTop = () => {
      if (!supportSlideRef.current) return true;
      return supportSlideRef.current.scrollTop <= 5; // 5px 여유
    };

    const isAtBottom = () => {
      if (!supportSlideRef.current) return true;
      const { scrollTop, scrollHeight, clientHeight } = supportSlideRef.current;
      return scrollTop + clientHeight >= scrollHeight - 5; // 5px 여유
    };

    const handleWheel = (e: WheelEvent) => {
      // 푸터가 열려있으면 무조건 푸터 닫기 우선
      if (showFooter) {
        e.preventDefault();
        if (e.deltaY < 0) {
          // 위로 스크롤 시도 → 푸터 닫기
          setShowFooter(false);
        }
        return;
      }

      // 마지막 슬라이드일 때
      if (currentSlide === totalSlides - 1) {
        const atTop = isAtTop();
        const atBottom = isAtBottom();

        if (e.deltaY > 0 && atBottom) {
          // 맨 아래에서 아래로 스크롤 → 푸터 표시
          e.preventDefault();
          setShowFooter(true);
        } else if (e.deltaY < 0 && atTop) {
          // 맨 위에서 위로 스크롤 → 이전 슬라이드
          e.preventDefault();
          if (!isScrolling) {
            setIsScrolling(true);
            setTimeout(() => setIsScrolling(false), 800);
            prevSlide();
          }
        }
        // 중간에 있으면 내부 스크롤만 작동 (preventDefault 안 함)
        return;
      }

      // 다른 슬라이드에서는 기존 로직
      e.preventDefault();
      if (isScrolling) return;

      setIsScrolling(true);
      setTimeout(() => setIsScrolling(false), 800);

      if (e.deltaY > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isScrolling) return;

      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        prevSlide();
      } else if (e.key >= '1' && e.key <= '4') {
        e.preventDefault();
        goToSlide(parseInt(e.key) - 1);
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isScrolling) return;

      touchEndY = e.changedTouches[0].clientY;
      const swipeDistance = touchStartY - touchEndY;

      if (Math.abs(swipeDistance) > minSwipeDistance) {
        // 푸터가 열려있으면 무조건 푸터 닫기 우선
        if (showFooter) {
          if (swipeDistance < 0) {
            // 아래로 스와이프 → 푸터 닫기
            setShowFooter(false);
          }
          return;
        }

        // 마지막 슬라이드일 때
        if (currentSlide === totalSlides - 1) {
          const atTop = isAtTop();
          const atBottom = isAtBottom();

          if (swipeDistance > 0 && atBottom) {
            // 맨 아래에서 위로 스와이프 → 푸터 표시
            setShowFooter(true);
          } else if (swipeDistance < 0 && atTop) {
            // 맨 위에서 아래로 스와이프 → 이전 슬라이드
            setIsScrolling(true);
            setTimeout(() => setIsScrolling(false), 800);
            prevSlide();
          }
          // 중간에 있으면 내부 스크롤만 작동
          return;
        }

        // 다른 슬라이드에서는 기존 로직
        setIsScrolling(true);
        setTimeout(() => setIsScrolling(false), 800);

        if (swipeDistance > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [nextSlide, prevSlide, goToSlide, isScrolling, currentSlide, totalSlides, showFooter]);

  const scrollToNext = () => {
    nextSlide();
  };

  const categories = [
    { name: t('products.categories.all'), icon: <FilterIcon /> },
    { name: t('products.categories.pig'), icon: <PigIcon /> },
    { name: t('products.categories.poultry'), icon: <DrumstickIcon /> },
    { name: t('products.categories.beef'), icon: <CowIcon2 /> },
    { name: t('products.categories.fish'), icon: <FishIcon /> },
    { name: t('products.categories.pet'), icon: <PawIcon /> }
  ];

  // 카테고리별 아이콘 매핑
  const getCategoryIcon = (category: string) => {
    if (category.includes('양돈')) return <PigIcon />;
    if (category.includes('양계')) return <ChickenIcon />;
    if (category.includes('축우')) return <CowIcon2 />;
    if (category.includes('양어')) return <FishIcon />;
    if (category.includes('반려동물')) return <PawIcon />;
    return <LeafIcon />;
  };

  // API 데이터에 이미지와 아이콘 추가
  const productsWithImages = products.map(product => {
    return {
      ...product,
      bgImage: productImageMap[product.id] || '/img/product_Icon.png',
      icon: getCategoryIcon(product.category)
    };
  });

  // 모든 제품이 전 축종 사용 가능하므로 카테고리와 관계없이 모든 제품 표시
  const filteredProducts = productsWithImages;

  // 뉴스 카테고리별 배경색 생성
  const getNewsBgColor = (index: number): string => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    ];
    return colors[index % colors.length];
  };

  // 뉴스 카테고리별 아이콘 생성
  const getNewsIcon = (index: number): React.ReactNode => {
    const icons = [<NewsIcon />, <ShieldIcon />, <UsersIcon />, <LeafIcon />, <NewsIcon />, <HeartIcon />];
    return icons[index % icons.length];
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <Container $showFooter={showFooter} className="home-container">
      {/* 슬라이드 네비게이션 */}
      <SlideNavigation>
        {Array.from({ length: totalSlides }, (_, index) => (
          <NavDot
            key={index}
            $active={currentSlide === index}
            $currentSlide={currentSlide}
            onClick={() => goToSlide(index)}
            title={[
              t('common.slideNav.home'),
              t('common.slideNav.about'),
              t('common.slideNav.products'),
              t('common.slideNav.news'),
              t('common.slideNav.contact')
            ][index]}
          />
        ))}
      </SlideNavigation>

      {/* 슬라이드 컨트롤 */}
      <SlideControls>
        <ControlButton
          onClick={prevSlide}
          disabled={currentSlide === 0}
          title="이전 슬라이드"
        >
          <ArrowUpIcon />
        </ControlButton>
        <ControlButton
          onClick={nextSlide}
          disabled={currentSlide === totalSlides - 1}
          title="다음 슬라이드"
        >
          <ArrowDownIcon />
        </ControlButton>
      </SlideControls>

      {/* 모바일 스와이프 가이드 */}
      <SwipeGuide $show={showSwipeGuide && currentSlide === 0}>
        {t('hero.swipeGuide')}
      </SwipeGuide>

      {/* 배경 로고 */}
      <BackgroundLogo $visible={currentSlide === 1}>
        NAWBIO
      </BackgroundLogo>

      <SlidesContainer $currentSlide={currentSlide}>
        {/* HERO 슬라이드 */}
        <HeroSlide id="hero">
          <HeroBackgroundVideo ref={heroVideoRef} loop muted playsInline preload="metadata">
            <source src="/img/main_Piglet.webm" type="video/webm" />
          </HeroBackgroundVideo>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('hero.title').split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i === 0 && <br />}
              </React.Fragment>
            ))}
          </HeroTitle>
          <HeroSlogan
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {t('hero.subtitle')}
          </HeroSlogan>
        </HeroContent>

        <ScrollIndicator onClick={scrollToNext}>
          <ScrollText>Scroll</ScrollText>
          <ScrollArrow
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDownIcon />
          </ScrollArrow>
        </ScrollIndicator>
        </HeroSlide>

        {/* 기업 소개 슬라이드 */}
        <AboutSlide id="about">
          <AboutContent>
            <AboutTextSection>
              <AboutHeading
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {t('about.heading')}
              </AboutHeading>
              <AboutText
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                {t('about.description')}
              </AboutText>
              <LearnMoreButton
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/about')}
              >
                {t('about.learnMore')}
              </LearnMoreButton>
            </AboutTextSection>

            <AboutImageSection
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img src="/img/Herosection_Pig.png" alt="나우바이오" />
            </AboutImageSection>
          </AboutContent>
        </AboutSlide>

        {/* 제품 슬라이드 */}
        <ProductsSlide id="products">
        <SectionContent>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t('products.title')}
          </SectionTitle>
          <SectionDescription
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {t('products.subtitle')}
          </SectionDescription>

          <CategoryTabs>
            {categories.map(category => (
              <CategoryTab
                key={category.name}
                $active={activeCategory === category.name}
                onClick={() => setActiveCategory(category.name)}
              >
                {category.icon}
                {category.name}
              </CategoryTab>
            ))}
          </CategoryTabs>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
              {t('products.loading') || 'Loading products...'}
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#e74c3c' }}>
              {t('products.error') || 'Error loading products'}: {error}
            </div>
          )}

          {!loading && !error && (
            <ProductsSliderContainer>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                breakpoints={{
                  640: {
                    slidesPerView: 1,
                    spaceBetween: 20
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 30
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 30
                  }
                }}
              >
                {filteredProducts.map((product) => (
                  <SwiperSlide key={product.id}>
                    <ProductCard
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      viewport={{ once: true }}
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <ProductImageContainer $bgColor={product.bgColor}>
                        {(() => {
                          const imageUrl = product.bgImage || '/img/product_Icon.png';
                          const isVideo = imageUrl.endsWith('.webm') || imageUrl.endsWith('.mp4');

                          if (isVideo) {
                            return (
                              <LazyVideo src={imageUrl} />
                            );
                          }
                          return <ProductMediaImage $bgImage={imageUrl} />;
                        })()}
                      </ProductImageContainer>
                      <ProductInfo>
                        <ProductTitle>{product.name}</ProductTitle>
                        <ProductDescription>{product.excerpt || product.description}</ProductDescription>
                        <ViewMoreButton>{t('products.viewDetails')} →</ViewMoreButton>
                      </ProductInfo>
                    </ProductCard>
                  </SwiperSlide>
                ))}
              </Swiper>
            </ProductsSliderContainer>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            style={{ marginTop: '40px' }}
          >
            <ViewMoreButton onClick={() => navigate('/products')}>
              {t('products.viewAll')} →
            </ViewMoreButton>
          </motion.div>
        </SectionContent>
        </ProductsSlide>

        {/* 새소식 슬라이드 */}
        <NewsSlide id="news">
        <SectionContent>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t('news.title')}
          </SectionTitle>
          <SectionDescription
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {t('news.subtitle')}
          </SectionDescription>

          {newsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#666' }}>
              Loading...
            </div>
          ) : newsData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#666' }}>
              {t('news.noNews') || '게시된 뉴스가 없습니다.'}
            </div>
          ) : (
            <NewsSliderContainer>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 4000, disableOnInteraction: false }}
                breakpoints={{
                  640: {
                    slidesPerView: 1,
                    spaceBetween: 20
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 30
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 30
                  }
                }}
              >
                {newsData.map((news, index) => (
                  <SwiperSlide key={news.id}>
                    <NewsCard
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      viewport={{ once: true }}
                      onClick={() => navigate('/news')}
                    >
                      <NewsImage $bgColor={getNewsBgColor(index)} $thumbnailUrl={news.thumbnailUrl || undefined}>
                        {!news.thumbnailUrl && getNewsIcon(index)}
                        <NewsDateBadge>{formatDate(news.publishedAt || news.createdAt)}</NewsDateBadge>
                      </NewsImage>
                      <NewsContent>
                        <NewsTitle>{news.title}</NewsTitle>
                        <NewsExcerpt>{news.excerpt}</NewsExcerpt>
                      </NewsContent>
                    </NewsCard>
                  </SwiperSlide>
                ))}
              </Swiper>
            </NewsSliderContainer>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            style={{ marginTop: '40px' }}
          >
            <ViewMoreButton onClick={() => navigate('/news')}>
              {t('news.viewAll')} →
            </ViewMoreButton>
          </motion.div>
        </SectionContent>
        </NewsSlide>

        {/* 문의 슬라이드 */}
        <SupportSlide id="support" ref={supportSlideRef} $isActive={currentSlide === 4}>
        <SectionContent>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t('contact.title')}
          </SectionTitle>
          <SectionDescription
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {t('contact.subtitle')}
          </SectionDescription>

          <SupportContent>
            <ContactInfoGrid>
              <ContactInfoCard
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <ContactIcon>
                  <FaPhone />
                </ContactIcon>
                <ContactLabel>{t('contact.info.phone')}</ContactLabel>
                <ContactValue>{t('about.location.headquarters.tel')}</ContactValue>
                <ContactValue style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                  {t('contact.info.phoneHours')}
                </ContactValue>
              </ContactInfoCard>

              <ContactInfoCard
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <ContactIcon>
                  <FaEnvelope />
                </ContactIcon>
                <ContactLabel>{t('contact.info.email')}</ContactLabel>
                <ContactValue>{t('about.company.emailValue')}</ContactValue>
                <ContactValue style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                  {t('contact.info.emailHours')}
                </ContactValue>
              </ContactInfoCard>

              <ContactInfoCard
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <ContactIcon>
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactLabel>{t('contact.info.address')}</ContactLabel>
                <ContactValue style={{ fontSize: '0.85rem' }}>{t('about.location.headquarters.address')}</ContactValue>
              </ContactInfoCard>
            </ContactInfoGrid>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <ContactButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/inquiry')}
              >
                {t('inquiry.writeButton')}
              </ContactButton>
            </motion.div>
          </SupportContent>
        </SectionContent>
        </SupportSlide>
      </SlidesContainer>

      {/* 푸터 */}
      {showFooter && (
        <motion.div
          initial={{ y: '100vh' }}
          animate={{ y: 0 }}
          exit={{ y: '100vh' }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            top: '100vh',
            left: 0,
            right: 0,
            zIndex: 1001
          }}
        >
          <Footer />
        </motion.div>
      )}
    </Container>
  );
};

export default HomePage;
